import { CommonModule } from "@angular/common";
import { Component, OnInit, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { AlertComponent } from "@components/alert/alert.component";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxToggleComponent } from "@components/checkbox-side/checkbox-toggle.component";
import { ComboboxComponent } from "@components/combobox/combobox.component";
import { RadioComponent } from "@components/radio/radio.component";
import { SelectComponent } from "@components/select/select.component";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { SelectValue } from "app/interfaces/select-value.interface";
import { UserUpdateRequest } from "app/interfaces/user-update-request.interface";
import { ApiService, UserInfo } from "app/services/api.service";

@Component({
    selector: 'app-user',
    templateUrl: './user.component.html',
    styleUrl: './user.component.scss',
    imports: [CommonModule, TranslateModule, FormsModule, SelectComponent, ButtonComponent, CheckboxToggleComponent, ComboboxComponent, AlertComponent,RadioComponent],
    standalone: true,
})
export class UserComponent implements OnInit {
    userInfo: UserInfo

    name: string = ''
    lastName: string = ''
    position: string = ''
    selectedPosition: SelectValue | null = null
    phone: string = ''
    email: string = ''
    hasWeAccess: boolean = false
    hasEcosentinoAccess: boolean = false
    hasFinancialPortalAccess: boolean = false
    hasNexusAccess: boolean = false
    notificationsFromLeads: boolean = false

    originalUserInfo: UserUpdateRequest

    hasChanges = false
    positions: SelectValue[] = [
        { id: 'my_profile.user_section.position.director', label: this.translate.instant('my_profile.user_section.position.director'), value: 'Director' },
        {
            id: 'my_profile.user_section.position.executive_vice_president',
            label: this.translate.instant('my_profile.user_section.position.executive_vice_president'),
            value: 'Executive Vice President',
        },
        { id: 'my_profile.user_section.position.chief_of_staff', label: this.translate.instant('my_profile.user_section.position.chief_of_staff'), value: 'Chief of Staff' },
        { id: 'my_profile.user_section.position.architect', label: this.translate.instant('my_profile.user_section.position.architect'), value: 'Architect' },
        { id: 'my_profile.user_section.position.design_staff', label: this.translate.instant('my_profile.user_section.position.design_staff'), value: 'Design Staff' },
        { id: 'my_profile.user_section.position.project_leader', label: this.translate.instant('my_profile.user_section.position.project_leader'), value: 'Project Leader' },
        {
            id: 'my_profile.user_section.position.commercial_interior_designer',
            label: this.translate.instant('my_profile.user_section.position.commercial_interior_designer'),
            value: 'Commercial Interior Designer',
        },
        {
            id: 'my_profile.user_section.position.assistant_vice_president',
            label: this.translate.instant('my_profile.user_section.position.assistant_vice_president'),
            value: 'Assistant Vice President',
        },
        {
            id: 'my_profile.user_section.position.marketing_director',
            label: this.translate.instant('my_profile.user_section.position.marketing_director'),
            value: 'Marketing Director',
        },
        {
            id: 'my_profile.user_section.position.construction_manager',
            label: this.translate.instant('my_profile.user_section.position.construction_manager'),
            value: 'Construction Manager',
        },
        {
            id: 'my_profile.user_section.position.director_or_vp_of_purchasing',
            label: this.translate.instant('my_profile.user_section.position.director_or_vp_of_purchasing'),
            value: 'Director or VP of Purchasing',
        },
        {
            id: 'my_profile.user_section.position.sales_representative',
            label: this.translate.instant('my_profile.user_section.position.sales_representative'),
            value: 'Sales Representative',
        },
        {
            id: 'my_profile.user_section.position.customer_service_representative',
            label: this.translate.instant('my_profile.user_section.position.customer_service_representative'),
            value: 'Customer Service Representative',
        },
        { id: 'my_profile.user_section.position.owner', label: this.translate.instant('my_profile.user_section.position.owner'), value: 'Owner' },
    ]

    constructor(private api: ApiService, private translate: TranslateService) {}

    ngOnInit(): void {
        this.api.userInfo.pipe().subscribe((userInfo) => {
            this.userInfo = userInfo
            this.name = this.userInfo.firstName ?? ''
            this.lastName = this.userInfo.lastName ?? ''
            
            this.phone = this.userInfo.phone ?? ''
            console.log('Phone loaded:', this.phone);
            console.log('Phone from userinfo:', this.userInfo.phone)
            this.email = this.userInfo.email ?? ''
            this.position = this.userInfo.position ?? '' //Review
            this.selectedPosition = this.positions.find(pos => pos.value === this.position) || null
            this.hasWeAccess = this.userInfo.hasWeAccess ?? false
            this.hasEcosentinoAccess = this.userInfo.hasEcosentinoAccess ?? false
            this.hasFinancialPortalAccess = this.userInfo.hasFinancialPortalAccess ?? false
            this.hasNexusAccess = this.userInfo.hasNexusAccess ?? false
            this.notificationsFromLeads = this.userInfo.hasNotificationsFromLeads ?? false

            this.originalUserInfo = {
                firstName: this.name,
                lastName: this.lastName,
                position: this.position || '',
                phone: this.phone,
                email: this.email,
            }
        })
    }

    checkForChanges(): void {
        this.hasChanges = this.hasUserInfoChanged()
    }

    hasUserInfoChanged(): boolean {
        return (
            JSON.stringify(this.originalUserInfo) !==
            JSON.stringify({
                name: this.name,
                lastName: this.lastName,
                position: this.position,
                phone: this.phone,
                email: this.email,
            })
        )
    }

    onSaveAll(): void {
        const updates: Promise<any>[] = []

        let updatedUserInfo: UserUpdateRequest | null = null

        if (this.hasUserInfoChanged()) {
            updatedUserInfo = {
                firstName: this.name,
                lastName: this.lastName,
                position: this.position,
                phone: this.phone,
                email: this.email,
            }
            updates.push(this.api.updateUserProfileInfo(updatedUserInfo).toPromise())
        }

        if (updates.length === 0) {
            console.log('No hay cambios que guardar')
            return
        }

        Promise.all(updates)
            .then(([businessResult]) => {
                console.log('Cambios guardados correctamente')

                if (businessResult === true && updatedUserInfo) {
                    this.originalUserInfo = updatedUserInfo

                    const updatedUserInfoLocalStorage: UserInfo = {
                        ...this.userInfo,
                        firstName: this.name,
                        lastName: this.lastName,
                        // position: this.position, //REVIEW
                        phone: this.phone,
                        email: this.email,
                    }

                    this.api.updateUserInfo(updatedUserInfoLocalStorage)
                } else {
                    this.name = this.userInfo.firstName ?? ''
                    this.lastName = this.userInfo.lastName ?? ''
                    // this.position = this.userInfo.position ?? ''; REVIEW
                    this.phone = this.userInfo.phone ?? ''
                    this.email = this.userInfo.email ?? ''
                }

                this.hasChanges = false
            })
            .catch((err) => {
                console.error('Error al guardar cambios', err)
            })
    }
    onAllowWeAccess(allowed: boolean) {
        this.hasWeAccess = allowed
        this.api.updateAccountAllowAccess(allowed).subscribe()
    }
    onAllowEcosentinoAccess(allowed: boolean) {
        this.hasEcosentinoAccess = allowed
        this.api.updateAccountEcosentinoAccess(allowed).subscribe()
    }
    onAllowFinancialPortalAccess(allowed: boolean) {
        this.hasFinancialPortalAccess = allowed
        this.api.updateAccountFinancialPortalAccess(allowed).subscribe()
    }
    onAllowNexusAccess(allowed: boolean) {
        this.hasNexusAccess = allowed
        this.api.updateAccountNexusAccess(allowed).subscribe()
    }
    onAllowNotificationsFromLeads(allowed: boolean) {
        this.notificationsFromLeads = allowed
        this.api.updateAccountNotificationsFromLeads(allowed).subscribe()
    }

    getUserPositions() {
        return this.positions
    }

    selectUserPosition($event: SelectValue | null) {
        this.position = $event?.value
        this.selectedPosition = $event
        this.checkForChanges()
    }
}